import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  FileText,
  Send,
  Eye,
  CheckCircle,
  CheckCircle2,
  Clock,
  X,
  Ban,
  Bell,
  RefreshCw,
  Download,
  PenTool,
  User,
  Mail,
  Shield,
  Copy,
  Check,
  Trash2,
  ExternalLink,
  XCircle,
  FilePlus,
  Loader2,
  AlertTriangle,
  Globe,
  Monitor,
  Briefcase,
} from "lucide-react";
import { esignApi } from "../../services/api";
import VoidEnvelopeModal from "./VoidEnvelopeModal";
import ResendReminderModal from "./ResendReminderModal";

// ─── Constants ───────────────────────────────────────────────────────────────

const SIGNER_COLORS = ["#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#EC4899"];

const STATUS_CONFIG: Record<
  string,
  { label: string; bg: string; text: string; icon: React.ElementType; dotColor: string }
> = {
  draft: { label: "Draft", bg: "bg-slate-100", text: "text-slate-600", icon: FileText, dotColor: "bg-slate-400" },
  sent: { label: "Sent", bg: "bg-blue-100", text: "text-blue-700", icon: Send, dotColor: "bg-blue-500" },
  viewed: { label: "Viewed", bg: "bg-amber-100", text: "text-amber-700", icon: Eye, dotColor: "bg-amber-500" },
  signed: { label: "Signed", bg: "bg-emerald-100", text: "text-emerald-700", icon: CheckCircle, dotColor: "bg-emerald-500" },
  voided: { label: "Voided", bg: "bg-red-100", text: "text-red-700", icon: Ban, dotColor: "bg-red-500" },
  expired: { label: "Expired", bg: "bg-slate-100", text: "text-slate-500", icon: Clock, dotColor: "bg-slate-400" },
};

const SIGNER_STATUS_CONFIG: Record<string, { label: string; bg: string; text: string }> = {
  pending: { label: "Pending", bg: "bg-slate-100", text: "text-slate-600" },
  sent: { label: "Sent", bg: "bg-blue-100", text: "text-blue-700" },
  viewed: { label: "Viewed", bg: "bg-amber-100", text: "text-amber-700" },
  signed: { label: "Signed", bg: "bg-emerald-100", text: "text-emerald-700" },
  declined: { label: "Declined", bg: "bg-red-100", text: "text-red-700" },
};

const EMAIL_STATUS_CONFIG: Record<
  string,
  { label: string; bg: string; text: string; dotColor: string }
> = {
  pending: { label: "Email Pending", bg: "bg-slate-50", text: "text-slate-500", dotColor: "bg-slate-400" },
  sent: { label: "Email Sent", bg: "bg-blue-50", text: "text-blue-600", dotColor: "bg-blue-500" },
  delivered: { label: "Delivered", bg: "bg-green-50", text: "text-green-700", dotColor: "bg-green-500" },
  opened: { label: "Opened", bg: "bg-emerald-50", text: "text-emerald-700", dotColor: "bg-emerald-500" },
  bounced: { label: "Bounced", bg: "bg-red-50", text: "text-red-700", dotColor: "bg-red-500" },
  failed: { label: "Failed", bg: "bg-red-50", text: "text-red-700", dotColor: "bg-red-500" },
};

const ROLE_CONFIG: Record<string, { bg: string; text: string }> = {
  client: { bg: "bg-blue-50", text: "text-blue-700" },
  lawyer: { bg: "bg-purple-50", text: "text-purple-700" },
  witness: { bg: "bg-cyan-50", text: "text-cyan-700" },
  notary: { bg: "bg-amber-50", text: "text-amber-700" },
};

const AUDIT_ACTION_CONFIG: Record<
  string,
  { icon: React.FC<any>; color: string; bg: string; label: string }
> = {
  created: { icon: FilePlus, color: "text-slate-500", bg: "bg-slate-100", label: "Request Created" },
  sent: { icon: Send, color: "text-blue-500", bg: "bg-blue-50", label: "Request Sent" },
  viewed: { icon: Eye, color: "text-amber-500", bg: "bg-amber-50", label: "Document Viewed" },
  signed: { icon: CheckCircle, color: "text-emerald-500", bg: "bg-emerald-50", label: "Signer Signed" },
  declined: { icon: XCircle, color: "text-red-500", bg: "bg-red-50", label: "Signer Declined" },
  voided: { icon: Ban, color: "text-red-500", bg: "bg-red-50", label: "Request Voided" },
  completed: { icon: CheckCircle2, color: "text-emerald-600", bg: "bg-emerald-50", label: "Request Completed" },
  reminder_sent: { icon: Bell, color: "text-purple-500", bg: "bg-purple-50", label: "Reminder Sent" },
  resent: { icon: RefreshCw, color: "text-blue-500", bg: "bg-blue-50", label: "Request Resent" },
  deleted: { icon: Trash2, color: "text-slate-500", bg: "bg-slate-100", label: "Request Deleted" },
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function truncateUserAgent(ua: string, maxLen = 60): string {
  if (!ua) return "";
  return ua.length > maxLen ? ua.substring(0, maxLen) + "..." : ua;
}

function formatTimeAgo(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const diffMs = Date.now() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDays = Math.floor(diffHr / 24);
  if (diffDays === 1) return "1 day ago";
  if (diffDays < 7) return `${diffDays} days ago`;
  return formatDate(iso);
}

function formatAuditAction(action: string): string {
  const config = AUDIT_ACTION_CONFIG[action];
  if (config) return config.label;
  return action
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function EsignEnvelopeDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [envelope, setEnvelope] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [auditTrail, setAuditTrail] = useState<any[]>([]);
  const [copiedToken, setCopiedToken] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  // Modal states
  const [voidModalOpen, setVoidModalOpen] = useState(false);
  const [resendModalOpen, setResendModalOpen] = useState(false);
  const [resendMode, setResendMode] = useState<"resend" | "remind">("resend");
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const fetchEnvelope = useCallback(async () => {
    if (!id) return;
    try {
      const res = await esignApi.get(id);
      setEnvelope(res.data.data);
      // Also fetch audit trail
      try {
        const auditRes = await esignApi.auditTrail(id);
        setAuditTrail(auditRes.data.data || []);
      } catch {
        // Fall back to inline audit trail
        setAuditTrail(res.data.data?.auditTrail || []);
      }
    } catch {
      navigate("/esign");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchEnvelope();
  }, [fetchEnvelope]);

  const refreshEnvelope = () => {
    fetchEnvelope();
  };

  // ─── Action Handlers ────────────────────────────────────────────────────────

  const handleSend = async () => {
    if (!id) return;
    setActionLoading("send");
    setActionError(null);
    try {
      await esignApi.send(id);
      refreshEnvelope();
    } catch (err: any) {
      setActionError(err.response?.data?.error || "Failed to send request");
    } finally {
      setActionLoading(null);
    }
  };

  const handleDownload = async () => {
    if (!id) return;
    setActionLoading("download");
    setActionError(null);
    try {
      const res = await esignApi.download(id);
      window.open(res.data.data?.url || res.data.url, "_blank");
    } catch (err: any) {
      setActionError(err.response?.data?.error || "Failed to download");
    } finally {
      setActionLoading(null);
    }
  };

  const handleAuditCertificate = async () => {
    // The signed packet PDF already includes the audit certificate as an appendix
    if (!id) return;
    setActionLoading("audit-cert");
    setActionError(null);
    try {
      const res = await esignApi.download(id);
      window.open(res.data.data?.url || res.data.url, "_blank");
    } catch (err: any) {
      setActionError(err.response?.data?.error || "Failed to download audit certificate");
    } finally {
      setActionLoading(null);
    }
  };

  const handleCopyLink = (token: string) => {
    const url = `${window.location.origin}/sign/${token}`;
    navigator.clipboard.writeText(url);
    setCopiedToken(token);
    setTimeout(() => setCopiedToken(null), 2000);
  };

  const handleDelete = async () => {
    if (!id) return;
    setActionLoading("delete");
    setActionError(null);
    try {
      await esignApi.softDelete(id);
      navigate("/esign");
    } catch (err: any) {
      setActionError(err.response?.data?.error || "Failed to delete request");
    } finally {
      setActionLoading(null);
    }
  };

  const handleResendSigner = async (signerIndex: number) => {
    if (!id) return;
    setActionLoading(`resend-${signerIndex}`);
    setActionError(null);
    try {
      await esignApi.resend(id, signerIndex);
      refreshEnvelope();
    } catch (err: any) {
      setActionError(err.response?.data?.error || "Failed to resend");
    } finally {
      setActionLoading(null);
    }
  };

  const handleRemindSigner = async (signerIndex: number) => {
    if (!id) return;
    setActionLoading(`remind-${signerIndex}`);
    setActionError(null);
    try {
      await esignApi.remind(id, signerIndex);
      refreshEnvelope();
    } catch (err: any) {
      setActionError(err.response?.data?.error || "Failed to send reminder");
    } finally {
      setActionLoading(null);
    }
  };

  // ─── Loading State ──────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2
          className="h-8 w-8 animate-spin"
          style={{ color: "var(--color-primary)" }}
        />
      </div>
    );
  }

  if (!envelope) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <AlertTriangle className="h-12 w-12 text-slate-300 mb-4" />
        <p className="text-slate-500">Signing request not found</p>
        <button
          onClick={() => navigate("/esign")}
          className="mt-4 text-sm font-medium hover:underline"
          style={{ color: "var(--color-primary)" }}
        >
          Back to E-Signatures
        </button>
      </div>
    );
  }

  const statusCfg = STATUS_CONFIG[envelope.status] || STATUS_CONFIG.draft;
  const StatusIcon = statusCfg.icon;
  const matterId =
    typeof envelope.matterId === "object" && envelope.matterId
      ? envelope.matterId._id
      : envelope.matterId;
  const matterTitle =
    typeof envelope.matterId === "object" && envelope.matterId
      ? envelope.matterId.title
      : null;

  const isActionable = envelope.status === "sent" || envelope.status === "viewed";
  const isDraft = envelope.status === "draft";
  const isSigned = envelope.status === "signed";
  const isTerminal = envelope.status === "voided" || envelope.status === "expired";

  // Use audit trail from either dedicated endpoint or inline
  const displayAuditTrail =
    auditTrail.length > 0 ? auditTrail : envelope.auditTrail || [];

  return (
    <div className="max-w-7xl mx-auto">
      {/* Back Button */}
      <button
        onClick={() => navigate("/esign")}
        className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-4 transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to E-Signatures
      </button>

      {/* Header Row */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6"
      >
        <div className="min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="text-2xl font-bold text-slate-800 truncate">
              {envelope.subject}
            </h2>
            <span
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${statusCfg.bg} ${statusCfg.text}`}
            >
              <StatusIcon className="h-3.5 w-3.5" />
              {statusCfg.label}
            </span>
          </div>
          <p className="text-sm text-slate-500 mt-1">
            Created {formatDate(envelope.createdAt)}
            {envelope.message && (
              <span className="ml-2 text-slate-400">
                &mdash; {envelope.message}
              </span>
            )}
          </p>
        </div>

        {/* Quick Actions in Header */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {isDraft && (
            <>
              <button
                onClick={handleSend}
                disabled={actionLoading === "send"}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-xl transition-colors disabled:opacity-50"
                style={{ backgroundColor: "var(--color-primary)" }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = "var(--color-primary-hover)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor = "var(--color-primary)")
                }
              >
                <Send className="h-4 w-4" />
                {actionLoading === "send" ? "Sending..." : "Send for Signing"}
              </button>
              <button
                onClick={() => setDeleteConfirmOpen(true)}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-xl transition-colors"
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </button>
            </>
          )}
          {isActionable && (
            <>
              <button
                onClick={() => {
                  setResendMode("remind");
                  setResendModalOpen(true);
                }}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-xl hover:bg-amber-100 transition-colors"
              >
                <Bell className="h-4 w-4" />
                Send Reminder
              </button>
              <button
                onClick={() => {
                  setResendMode("resend");
                  setResendModalOpen(true);
                }}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-xl hover:bg-blue-100 transition-colors"
              >
                <RefreshCw className="h-4 w-4" />
                Resend
              </button>
              <button
                onClick={() => setVoidModalOpen(true)}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-xl transition-colors"
              >
                <Ban className="h-4 w-4" />
                Void
              </button>
            </>
          )}
          {isSigned && (
            <>
              <button
                onClick={handleDownload}
                disabled={actionLoading === "download"}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-xl transition-colors disabled:opacity-50"
                style={{ backgroundColor: "var(--color-primary)" }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = "var(--color-primary-hover)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor = "var(--color-primary)")
                }
              >
                <Download className="h-4 w-4" />
                {actionLoading === "download" ? "Downloading..." : "Download Signed Packet"}
              </button>
              <button
                onClick={handleAuditCertificate}
                disabled={actionLoading === "audit-cert"}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors disabled:opacity-50"
              >
                <Shield className="h-4 w-4" />
                {actionLoading === "audit-cert" ? "Loading..." : "Audit Certificate"}
              </button>
            </>
          )}
        </div>
      </motion.div>

      {/* Action Error Banner */}
      {actionError && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-2.5 px-4 py-3 mb-6 rounded-xl bg-red-50 border border-red-200"
        >
          <AlertTriangle className="h-4 w-4 text-red-500 flex-shrink-0" />
          <span className="text-sm text-red-700 flex-1">{actionError}</span>
          <button
            onClick={() => setActionError(null)}
            className="p-0.5 rounded text-red-400 hover:text-red-600 flex-shrink-0"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </motion.div>
      )}

      {/* 2-Column Layout */}
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Left Column — Documents */}
        <div className="lg:w-2/3 space-y-6">
          {/* Document Preview Section */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="bg-white rounded-xl shadow-sm border border-slate-200"
          >
            <div className="px-6 py-4 border-b border-slate-100">
              <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <FileText className="h-4 w-4 text-slate-400" />
                Documents ({envelope.documents?.length || 0})
              </h3>
            </div>

            <div className="p-4 space-y-3">
              {envelope.documents?.length > 0 ? (
                envelope.documents.map((doc: any, i: number) => {
                  const docTitle =
                    typeof doc.documentId === "object" && doc.documentId
                      ? doc.documentId.title
                      : "Document";
                  const fileSize =
                    typeof doc.documentId === "object" && doc.documentId?.fileSize
                      ? doc.documentId.fileSize
                      : null;

                  return (
                    <div
                      key={i}
                      className="flex items-center justify-between bg-slate-50 border border-slate-100 rounded-xl p-4"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="p-2 bg-white rounded-lg border border-slate-200">
                          <FileText className="h-5 w-5 text-slate-400" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-slate-800 truncate">
                            {docTitle}
                          </p>
                          {fileSize && (
                            <p className="text-xs text-slate-400">
                              {(fileSize / 1024).toFixed(0)} KB
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {doc.requiresSignature && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 text-amber-700 rounded-full text-xs font-medium">
                            <PenTool className="h-3 w-3" />
                            Requires signature
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-8 text-sm text-slate-400">
                  No documents attached
                </div>
              )}
            </div>

            {/* Document Preview Placeholder */}
            {envelope.documents?.length > 0 && (
              <div className="px-4 pb-4">
                <div className="bg-slate-50 rounded-xl p-8 text-center border border-dashed border-slate-200">
                  <FileText className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-sm font-medium text-slate-600 mb-1">
                    Document Preview
                  </p>
                  <p className="text-xs text-slate-400 mb-4">
                    Download the document to view its full contents
                  </p>
                  <button
                    onClick={handleDownload}
                    disabled={actionLoading === "download"}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-xl transition-colors disabled:opacity-50"
                    style={{ backgroundColor: "var(--color-primary)" }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.backgroundColor = "var(--color-primary-hover)")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.backgroundColor = "var(--color-primary)")
                    }
                  >
                    <Download className="h-4 w-4" />
                    {actionLoading === "download" ? "Downloading..." : "Download to View"}
                  </button>
                </div>
              </div>
            )}

            {/* Field Placement Summary */}
            {envelope.fields?.length > 0 && (
              <div className="px-6 py-4 border-t border-slate-100">
                <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
                  Field Placements ({envelope.fields.length})
                </h4>
                <div className="flex flex-wrap gap-2">
                  {envelope.fields.map((field: any, i: number) => (
                    <span
                      key={i}
                      className="inline-flex items-center gap-1 px-2.5 py-1 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-600"
                    >
                      <PenTool className="h-3 w-3 text-slate-400" />
                      {field.type || "signature"}
                      {field.signerIndex !== undefined && (
                        <span
                          className="ml-1 w-2 h-2 rounded-full inline-block"
                          style={{
                            backgroundColor:
                              SIGNER_COLORS[field.signerIndex % SIGNER_COLORS.length],
                          }}
                        />
                      )}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        </div>

        {/* Right Column — Status, Signers, Audit */}
        <div className="lg:w-1/3 space-y-6">
          {/* Status & Actions Card */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl shadow-sm border border-slate-200 p-5"
          >
            {/* Large Status Badge */}
            <div className="flex items-center gap-3 mb-4">
              <div className={`p-2.5 rounded-xl ${statusCfg.bg}`}>
                <StatusIcon className={`h-5 w-5 ${statusCfg.text}`} />
              </div>
              <div>
                <span
                  className={`text-lg font-bold ${statusCfg.text}`}
                >
                  {statusCfg.label}
                </span>
              </div>
            </div>

            {/* Status-specific info */}
            <div className="text-sm text-slate-600 mb-4 leading-relaxed">
              {envelope.status === "draft" && (
                <p>
                  Created on {formatDate(envelope.createdAt)}. Ready to configure and
                  send.
                </p>
              )}
              {envelope.status === "sent" && (
                <p>
                  Sent on {envelope.sentAt ? formatDateTime(envelope.sentAt) : formatDateTime(envelope.updatedAt || envelope.createdAt)}.
                  {envelope.expiresAt && (
                    <> Expires on {formatDate(envelope.expiresAt)}.</>
                  )}
                </p>
              )}
              {envelope.status === "viewed" && (
                <p>Viewed by signers. Waiting for signatures.</p>
              )}
              {envelope.status === "signed" && (
                <p>
                  Completed on{" "}
                  {envelope.completedAt
                    ? formatDateTime(envelope.completedAt)
                    : formatDateTime(envelope.updatedAt || envelope.createdAt)}
                  . All signers have signed.
                </p>
              )}
              {envelope.status === "voided" && (
                <p>
                  Voided on{" "}
                  {envelope.voidedAt
                    ? formatDateTime(envelope.voidedAt)
                    : formatDateTime(envelope.updatedAt || envelope.createdAt)}
                  .
                  {envelope.voidReason && (
                    <>
                      {" "}
                      Reason: <span className="font-medium">{envelope.voidReason}</span>
                    </>
                  )}
                </p>
              )}
              {envelope.status === "expired" && (
                <p>
                  Expired on{" "}
                  {envelope.expiresAt
                    ? formatDate(envelope.expiresAt)
                    : formatDate(envelope.updatedAt || envelope.createdAt)}
                  .
                </p>
              )}
            </div>

            {/* Actions (mobile-friendly duplicate of header actions) */}
            <div className="space-y-2 lg:hidden">
              {isDraft && (
                <>
                  <button
                    onClick={handleSend}
                    disabled={actionLoading === "send"}
                    className="flex items-center justify-center gap-2 w-full px-4 py-2.5 text-sm font-medium text-white rounded-xl transition-colors disabled:opacity-50"
                    style={{ backgroundColor: "var(--color-primary)" }}
                  >
                    <Send className="h-4 w-4" />
                    {actionLoading === "send" ? "Sending..." : "Send for Signing"}
                  </button>
                  <button
                    onClick={() => setDeleteConfirmOpen(true)}
                    className="flex items-center justify-center gap-1.5 w-full px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-xl transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete
                  </button>
                </>
              )}
              {isActionable && (
                <>
                  <button
                    onClick={() => {
                      setResendMode("remind");
                      setResendModalOpen(true);
                    }}
                    className="flex items-center justify-center gap-2 w-full px-4 py-2.5 text-sm font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-xl hover:bg-amber-100 transition-colors"
                  >
                    <Bell className="h-4 w-4" />
                    Send Reminder
                  </button>
                  <button
                    onClick={() => {
                      setResendMode("resend");
                      setResendModalOpen(true);
                    }}
                    className="flex items-center justify-center gap-2 w-full px-4 py-2.5 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-xl hover:bg-blue-100 transition-colors"
                  >
                    <RefreshCw className="h-4 w-4" />
                    Resend
                  </button>
                  <button
                    onClick={() => setVoidModalOpen(true)}
                    className="flex items-center justify-center gap-2 w-full px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-xl transition-colors"
                  >
                    <Ban className="h-4 w-4" />
                    Void
                  </button>
                </>
              )}
              {isSigned && (
                <>
                  <button
                    onClick={handleDownload}
                    disabled={actionLoading === "download"}
                    className="flex items-center justify-center gap-2 w-full px-4 py-2.5 text-sm font-medium text-white rounded-xl transition-colors disabled:opacity-50"
                    style={{ backgroundColor: "var(--color-primary)" }}
                  >
                    <Download className="h-4 w-4" />
                    {actionLoading === "download" ? "Downloading..." : "Download Signed Packet"}
                  </button>
                  <button
                    onClick={handleAuditCertificate}
                    disabled={actionLoading === "audit-cert"}
                    className="flex items-center justify-center gap-2 w-full px-4 py-2.5 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors disabled:opacity-50"
                  >
                    <Shield className="h-4 w-4" />
                    {actionLoading === "audit-cert" ? "Loading..." : "Audit Certificate"}
                  </button>
                </>
              )}
            </div>

            {/* Matter Link */}
            {matterId && matterTitle && (
              <div className="mt-4 pt-4 border-t border-slate-100">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                  Linked Matter
                </p>
                <button
                  onClick={() => navigate(`/matters/${matterId}`)}
                  className="inline-flex items-center gap-2 text-sm font-medium hover:underline transition-colors"
                  style={{ color: "var(--color-primary)" }}
                >
                  <Briefcase className="h-4 w-4" />
                  {matterTitle}
                  <ExternalLink className="h-3 w-3" />
                </button>
              </div>
            )}
          </motion.div>

          {/* Signers Section */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="bg-white rounded-xl shadow-sm border border-slate-200"
          >
            <div className="px-5 py-4 border-b border-slate-100">
              <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <User className="h-4 w-4 text-slate-400" />
                Signers ({envelope.signers?.length || 0})
              </h3>
            </div>

            <div className="p-4 space-y-3">
              {envelope.signers?.map((signer: any, i: number) => {
                const signerColor = SIGNER_COLORS[i % SIGNER_COLORS.length];
                const sStatus =
                  SIGNER_STATUS_CONFIG[signer.status] || SIGNER_STATUS_CONFIG.pending;
                const rConfig = ROLE_CONFIG[signer.role] || ROLE_CONFIG.client;

                // Count fields assigned to this signer
                const signerFields = (envelope.fields || []).filter(
                  (f: any) => f.signerIndex === i
                );
                const fieldTypes = signerFields.reduce(
                  (acc: Record<string, number>, f: any) => {
                    const type = f.type || "signature";
                    acc[type] = (acc[type] || 0) + 1;
                    return acc;
                  },
                  {}
                );

                return (
                  <div
                    key={i}
                    className="rounded-xl border border-slate-200 overflow-hidden"
                  >
                    {/* Colored top border */}
                    <div
                      className="h-1"
                      style={{ backgroundColor: signerColor }}
                    />
                    <div className="p-4">
                      {/* Name row */}
                      <div className="flex items-start justify-between mb-1">
                        <div className="flex items-center gap-2 min-w-0">
                          <div
                            className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                            style={{ backgroundColor: signerColor }}
                          />
                          <span className="text-sm font-semibold text-slate-800 truncate">
                            {signer.name}
                          </span>
                        </div>
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${sStatus.bg} ${sStatus.text}`}
                        >
                          {sStatus.label}
                        </span>
                      </div>

                      {/* Email */}
                      <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-2 ml-5">
                        <Mail className="h-3 w-3" />
                        {signer.email}
                      </div>

                      {/* Email delivery status */}
                      {signer.emailStatus && signer.emailStatus !== "pending" && (
                        <div className="ml-5 mb-2">
                          {(() => {
                            const eConfig =
                              EMAIL_STATUS_CONFIG[signer.emailStatus] ||
                              EMAIL_STATUS_CONFIG.pending;
                            const timestamp =
                              signer.emailOpenedAt ||
                              signer.emailDeliveredAt ||
                              signer.emailSentAt;
                            return (
                              <span
                                className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${eConfig.bg} ${eConfig.text}`}
                              >
                                <span
                                  className={`w-1.5 h-1.5 rounded-full ${eConfig.dotColor}`}
                                />
                                {eConfig.label}
                                {timestamp && (
                                  <span className="font-normal opacity-75">
                                    {formatTimeAgo(timestamp)}
                                  </span>
                                )}
                              </span>
                            );
                          })()}
                          {signer.emailStatus === "failed" && signer.emailError && (
                            <p className="text-xs text-red-500 mt-1 truncate max-w-[200px]" title={signer.emailError}>
                              {signer.emailError}
                            </p>
                          )}
                        </div>
                      )}

                      {/* Role & Order */}
                      <div className="flex items-center gap-2 ml-5 mb-3">
                        <span
                          className={`px-1.5 py-0.5 rounded text-xs font-medium capitalize ${rConfig.bg} ${rConfig.text}`}
                        >
                          {signer.role}
                        </span>
                        <span className="text-xs text-slate-400">
                          Order: {signer.order}
                        </span>
                      </div>

                      {/* Signed details */}
                      {signer.status === "signed" && signer.signedAt && (
                        <div className="bg-emerald-50/50 rounded-xl p-3 mb-3 space-y-1">
                          <div className="flex items-center gap-1.5 text-xs text-emerald-700">
                            <CheckCircle className="h-3 w-3" />
                            Signed {formatDateTime(signer.signedAt)}
                          </div>
                          {signer.ip && (
                            <div className="flex items-center gap-1.5 text-xs text-slate-500">
                              <Globe className="h-3 w-3" />
                              IP: {signer.ip}
                            </div>
                          )}
                          {signer.userAgent && (
                            <div className="flex items-center gap-1.5 text-xs text-slate-500">
                              <Monitor className="h-3 w-3" />
                              {truncateUserAgent(signer.userAgent)}
                            </div>
                          )}
                          {signer.geoLocation && (
                            <div className="flex items-center gap-1.5 text-xs text-slate-500">
                              <Globe className="h-3 w-3" />
                              {signer.geoLocation}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Declined details */}
                      {signer.status === "declined" && (
                        <div className="bg-red-50/50 rounded-xl p-3 mb-3">
                          <div className="flex items-center gap-1.5 text-xs text-red-700">
                            <XCircle className="h-3 w-3" />
                            Declined
                            {signer.declineReason && (
                              <span className="text-red-600 font-normal">
                                : {signer.declineReason}
                              </span>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Fields assigned */}
                      {signerFields.length > 0 && (
                        <div className="flex items-center gap-2 mb-3 ml-5">
                          <span className="text-xs text-slate-400">
                            {signerFields.length} field{signerFields.length !== 1 ? "s" : ""}:
                          </span>
                          <div className="flex flex-wrap gap-1">
                            {Object.entries(fieldTypes).map(([type, count]) => (
                              <span
                                key={type}
                                className="px-1.5 py-0.5 bg-slate-50 border border-slate-200 rounded text-xs text-slate-600"
                              >
                                {type}
                                {(count as number) > 1 && ` x${count}`}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Per-signer actions */}
                      {isActionable && signer.status !== "signed" && (
                        <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
                          <button
                            onClick={() => handleResendSigner(i)}
                            disabled={actionLoading === `resend-${i}`}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors disabled:opacity-50"
                          >
                            <RefreshCw className="h-3 w-3" />
                            {actionLoading === `resend-${i}` ? "..." : "Resend"}
                          </button>
                          <button
                            onClick={() => handleRemindSigner(i)}
                            disabled={actionLoading === `remind-${i}`}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-amber-700 bg-amber-50 rounded-lg hover:bg-amber-100 transition-colors disabled:opacity-50"
                          >
                            <Bell className="h-3 w-3" />
                            {actionLoading === `remind-${i}` ? "..." : "Remind"}
                          </button>
                          {signer.signingToken && (
                            <button
                              onClick={() => handleCopyLink(signer.signingToken)}
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-slate-600 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                            >
                              {copiedToken === signer.signingToken ? (
                                <>
                                  <Check className="h-3 w-3 text-emerald-600" />
                                  <span className="text-emerald-600">Copied!</span>
                                </>
                              ) : (
                                <>
                                  <Copy className="h-3 w-3" />
                                  Copy Link
                                </>
                              )}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}

              {(!envelope.signers || envelope.signers.length === 0) && (
                <div className="text-center py-6 text-sm text-slate-400">
                  No signers added
                </div>
              )}
            </div>
          </motion.div>

          {/* Audit Trail Section */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl shadow-sm border border-slate-200"
          >
            <div className="px-5 py-4 border-b border-slate-100">
              <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <Shield className="h-4 w-4 text-slate-400" />
                Audit Trail
              </h3>
            </div>

            <div className="p-4">
              {displayAuditTrail.length > 0 ? (
                <div className="relative">
                  {/* Timeline line */}
                  <div className="absolute left-[15px] top-2 bottom-2 w-0.5 bg-slate-100" />

                  <div className="space-y-1">
                    {displayAuditTrail.map((entry: any, i: number) => {
                      const config =
                        AUDIT_ACTION_CONFIG[entry.action] || AUDIT_ACTION_CONFIG.created;
                      const ActionIcon = config.icon;

                      return (
                        <motion.div
                          key={`${entry.at}-${i}`}
                          initial={{ opacity: 0, x: -6 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: Math.min(i * 0.03, 0.6) }}
                          className="relative flex items-start gap-3 py-2.5 pl-0"
                        >
                          <div
                            className={`relative z-10 p-1.5 rounded-full flex-shrink-0 ${config.bg}`}
                          >
                            <ActionIcon className={`h-3.5 w-3.5 ${config.color}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-sm font-medium text-slate-800">
                                {formatAuditAction(entry.action)}
                              </span>
                            </div>
                            {entry.actor && (
                              <p className="text-xs text-slate-500 mt-0.5">
                                by {entry.actor}
                              </p>
                            )}
                            <p className="text-xs text-slate-400 mt-0.5">
                              {formatDateTime(entry.at)}
                              {entry.ip && ` \u2022 IP: ${entry.ip}`}
                            </p>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="text-center py-6 text-sm text-slate-400">
                  No audit events yet
                </div>
              )}

              {/* Download Audit Certificate */}
              {displayAuditTrail.length > 0 && (
                <div className="mt-4 pt-3 border-t border-slate-100">
                  <button
                    onClick={handleAuditCertificate}
                    disabled={actionLoading === "audit-cert"}
                    className="inline-flex items-center gap-2 w-full justify-center px-4 py-2 text-sm font-medium text-slate-700 bg-slate-50 border border-slate-200 rounded-xl hover:bg-slate-100 transition-colors disabled:opacity-50"
                  >
                    <Download className="h-4 w-4" />
                    {actionLoading === "audit-cert"
                      ? "Loading..."
                      : "Download Audit Certificate"}
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </div>

      {/* ─── Modals ────────────────────────────────────────────────────────────── */}

      {/* Void Modal */}
      <VoidEnvelopeModal
        open={voidModalOpen}
        onClose={() => setVoidModalOpen(false)}
        onVoided={() => {
          setVoidModalOpen(false);
          refreshEnvelope();
        }}
        envelopeId={id!}
        envelopeSubject={envelope.subject}
      />

      {/* Resend / Remind Modal */}
      <ResendReminderModal
        open={resendModalOpen}
        onClose={() => setResendModalOpen(false)}
        onSent={() => {
          setResendModalOpen(false);
          refreshEnvelope();
        }}
        envelopeId={id!}
        signers={envelope.signers || []}
        mode={resendMode}
      />

      {/* Delete Confirmation Modal */}
      {deleteConfirmOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          onClick={() => setDeleteConfirmOpen(false)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-xl shadow-xl border border-slate-200 p-6 w-full max-w-md mx-4"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-50 rounded-xl">
                <Trash2 className="h-5 w-5 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-800">
                Delete Request
              </h3>
            </div>
            <p className="text-sm text-slate-500 mb-6">
              Are you sure you want to delete "{envelope.subject}"? This action
              cannot be undone.
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setDeleteConfirmOpen(false)}
                className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={actionLoading === "delete"}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-xl hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {actionLoading === "delete" ? "Deleting..." : "Delete"}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
